import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { AuthService } from '@app/core/services/Auth/auth-service';
import { ValidationConstants } from '@app/constants/validation.constants';
import { Spinner } from '../spinner/spinner';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-change-password-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    Spinner
  ],
  templateUrl: './change-password-form.html',
  styleUrls: ['./change-password-form.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChangePasswordFormComponent implements OnInit, OnDestroy {
  /** When true, user must change password and cannot dismiss the form. */
  @Input() forceRequired = false;
  @Output() close = new EventEmitter<void>();

  changePasswordForm: FormGroup;
  hideCurrentPassword = true;
  hideNewPassword = true;
  hideConfirmPassword = true;
  loading = signal(false);
  success = signal(false);
  error = signal<string | null>(null);
  /** True while logout API is in progress — drives Logout button spinner. */
  isLoggingOut = signal(false);
  /**
   * Latched forced mode for this panel instance.
   * Auth logout clears passwordResetRequired before navigation, which would otherwise
   * flip the @Input and briefly show the form without the welcome banner.
   */
  isForced = false;
  /** Display name for forced first-login / reset welcome banner. */
  welcomeUserName = 'User';
  readonly welcomeMediaSrc = 'assets/images/lock.gif';
  readonly passwordMaxLength = 128;
  private readonly passwordMinLength = 8;
  /** Same complexity as app password rule, with 8–128 length for this form. */
  private readonly passwordPattern =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,128}$/;
  private changePasswordSub: Subscription | null = null;

  private readonly fieldNameMap: Record<string, string> = {
    'old password': 'oldPassword',
    'current password': 'oldPassword',
    'oldpassword': 'oldPassword',
    'new password': 'newPassword',
    'newpassword': 'newPassword',
    'confirm password': 'confirmPassword',
    'confirmpassword': 'confirmPassword',
  };

  constructor(private fb: FormBuilder, private authservice: AuthService,private toastr: ToastrService
  ) {
    this.changePasswordForm = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(this.passwordMinLength),
        Validators.maxLength(this.passwordMaxLength),
        Validators.pattern(this.passwordPattern)
      ]],
      confirmPassword: ['', [
        Validators.required,
        Validators.maxLength(this.passwordMaxLength)
      ]]
    }, { validators: this.passwordMatchValidator });
    this.changePasswordForm.get('oldPassword')?.valueChanges.subscribe(() => {
      this.clearBackendError('oldPassword');
    });
    this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(() => {
      this.clearBackendError('newPassword');
      this.changePasswordForm.updateValueAndValidity({ emitEvent: false });
      const confirmControl = this.changePasswordForm.get('confirmPassword');
      if (confirmControl?.value) {
        confirmControl.markAsTouched();
      }
    });
    this.changePasswordForm.get('confirmPassword')?.valueChanges.subscribe(() => {
      this.clearBackendError('confirmPassword');
      this.changePasswordForm.updateValueAndValidity({ emitEvent: false });
    });
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const newPassword = group.get('newPassword')?.value ?? '';
    const confirmPassword = group.get('confirmPassword')?.value ?? '';
    if (!confirmPassword) {
      return null;
    }
    return newPassword === confirmPassword ? null : { mismatch: true };
  }

  onSubmit() {
    this.changePasswordForm.markAllAsTouched();
    this.clearAllBackendErrors();
    this.changePasswordForm.updateValueAndValidity();

    if (this.changePasswordForm.invalid) {
      // Field-level messages are shown under each input; skip toastr.
      return;
    }

    this.loading.set(true);
    this.success.set(false);
    this.error.set(null);

    const { oldPassword, newPassword } = this.changePasswordForm.value;
    this.changePasswordSub?.unsubscribe();
    this.changePasswordSub = this.authservice.changePassword(
      this.isForced ? { newPassword } : { oldPassword, newPassword }
    ).subscribe({
      next: (response: any) => {
        if (this.isLoggingOut()) {
          return;
        }
        this.toastr.success(response.message || 'Password changed successfully');
        this.loading.set(false);
        this.success.set(true);
        this.changePasswordForm.reset();
        this.authservice.setPasswordResetRequired(false);
        this.close.emit();
      },
      error: (err) => {
        if (this.isLoggingOut()) {
          return;
        }
        this.loading.set(false);
        this.applyApiFieldErrors(err);
      },
    });
  }

  /** Maps API field errors onto form controls so they render under inputs. */
  private applyApiFieldErrors(err: any): void {
    const payload = err?.error;
    const fieldErrors: Array<{ field?: string; message?: string }> = Array.isArray(payload?.error)
      ? payload.error
      : Array.isArray(payload?.errors)
        ? payload.errors
        : [];

    let appliedToField = false;

    for (const item of fieldErrors) {
      const controlName = this.resolveControlName(item?.field);
      const message = item?.message?.trim();
      if (!controlName || !message) {
        continue;
      }

      const control = this.changePasswordForm.get(controlName);
      if (!control) {
        continue;
      }

      control.setErrors({
        ...control.errors,
        backendError: true,
        backendErrorMessage: message,
      });
      control.markAsTouched();
      appliedToField = true;
    }

    if (appliedToField) {
      return;
    }

    const fallback =
      (typeof payload?.message === 'string' && payload.message) ||
      (typeof fieldErrors[0]?.message === 'string' && fieldErrors[0].message) ||
      'Failed to change password';
    this.toastr.error(fallback);
  }

  private resolveControlName(field?: string): string | null {
    if (!field) {
      return null;
    }
    const normalized = field.trim().toLowerCase().replace(/[_-]+/g, ' ');
    return this.fieldNameMap[normalized] ?? this.fieldNameMap[normalized.replace(/\s+/g, '')] ?? null;
  }

  private clearBackendError(controlName: string): void {
    const control = this.changePasswordForm.get(controlName);
    if (!control?.errors?.['backendError']) {
      return;
    }
    const { backendError, backendErrorMessage, ...remaining } = control.errors;
    void backendError;
    void backendErrorMessage;
    control.setErrors(Object.keys(remaining).length ? remaining : null);
  }

  private clearAllBackendErrors(): void {
    (['oldPassword', 'newPassword', 'confirmPassword'] as const).forEach((name) =>
      this.clearBackendError(name)
    );
  }

  onCancel() {
    if (this.isForced) {
      return;
    }
    this.close.emit();
  }

  onLogout(): void {
    if (!this.isForced || this.isLoggingOut()) {
      return;
    }
    // Allow logout even while Save API spinner is active.
    this.changePasswordSub?.unsubscribe();
    this.changePasswordSub = null;
    this.loading.set(false);
    this.isLoggingOut.set(true);
    this.authservice.logout().subscribe();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (!this.isForced || this.isLoggingOut()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  showFieldError(controlName: string): boolean {
    const control = this.changePasswordForm.get(controlName);

    if (control?.errors?.['backendError'] && control.touched) {
      return true;
    }

    if (controlName === 'confirmPassword') {
      if (control?.errors?.['required'] && control.touched) {
        return true;
      }
      if (this.changePasswordForm.hasError('mismatch') && (control?.touched || control?.dirty)) {
        return true;
      }
      return false;
    }

    return !!(control && control.invalid && control.touched);
  }

  getFieldError(controlName: string): string {
    const control = this.changePasswordForm.get(controlName);

    if (control?.errors?.['backendError']) {
      return control.errors['backendErrorMessage'] || 'An error occurred. Please try again.';
    }

    if (controlName === 'confirmPassword') {
      if (control?.errors?.['required'] && control.touched) {
        return 'Please confirm New Password';
      }
      // Prefer required over mismatch when confirm is empty
      if (
        this.changePasswordForm.hasError('mismatch') &&
        (control?.touched || control?.dirty) &&
        !control?.errors?.['required']
      ) {
        return 'Passwords do not match';
      }
      return '';
    }

    if (!control?.errors || !control.touched) {
      return '';
    }

    if (control.errors['required']) {
      switch (controlName) {
        case 'oldPassword':
          return 'Please enter Current Password';
        case 'newPassword':
          return 'Please enter New Password';
        default:
          return ValidationConstants.VALIDATION_MSG.passwordEmpty;
      }
    }

    if (controlName === 'newPassword') {
      if (control.errors['pattern'] || control.errors['minlength'] || control.errors['maxlength']) {
        return 'Password must be at least 8 characters with uppercase, lowercase, number, and special character.';
      }
    }

    if (control.errors['maxlength']) {
      return `Maximum ${this.passwordMaxLength} characters allowed`;
    }

    return '';
  }

  // Getter methods for form controls
  get oldPasswordControl(): FormControl {
    return this.changePasswordForm.get('oldPassword') as FormControl;
  }

  get newPasswordControl(): FormControl {
    return this.changePasswordForm.get('newPassword') as FormControl;
  }

  get confirmPasswordControl(): FormControl {
    return this.changePasswordForm.get('confirmPassword') as FormControl;
  }

  ngOnInit() {
    this.isForced = this.forceRequired;
    if (this.isForced) {
      this.oldPasswordControl.clearValidators();
      this.oldPasswordControl.updateValueAndValidity({ emitEvent: false });
    }
    document.body.classList.add('modal-open');
    this.welcomeUserName = this.resolveWelcomeUserName();
  }

  ngOnDestroy() {
    this.changePasswordSub?.unsubscribe();
    document.body.classList.remove('modal-open');
  }

  private resolveWelcomeUserName(): string {
    const fromUser = this.authservice.getUser()?.name;
    if (typeof fromUser === 'string' && fromUser.trim()) {
      return fromUser.trim();
    }
    const fromStorage = localStorage.getItem('userName');
    if (fromStorage?.trim()) {
      return fromStorage.trim();
    }
    return 'User';
  }
}
